import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, MisconfigurationsAppWebComponentSchema } from '../types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { actionsColumnRenderer, misconfigurationNameRenderer } from './CustomGridRenderers';
import { TemplateState } from '../../../types/common/types';
import { MisconfigurationsAppGridDataProvider } from '../dataProvider/MisconfigurationsAppGridDataProvider';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import {
  affectedDevicesRenderer,
  companyRenderer,
  complianceRenderer,
  osRenderer,
  regionsRenderer,
  riskScoreRenderer,
} from '../../common/customRenderers';
import { FixStateBulkValues, MisconfigurationType, RiskTypes } from '../../common/constants';
import {
  changeEmptyStateIcon,
  getAffectedDeviceTypeColumnValues,
  getCompanyFilterUiConfig,
  getComplianceColumnValues,
  getMitigationTypeColumnValues,
  getOSTypeColumnValues,
  getStateColumnValues,
  getStatusColumnValues,
  getWatchlistColumnValues,
  processExternalFilters,
  setBulkActions,
  setComplianceDisclaimer,
  setGridCommonProperties,
  setTranslatedStrings,
} from '../../common/utils';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { convertGenericViewConfigToGridViewConfig } from '../../../grid/genericGridSmartViews/types';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { ElementClass } from '../../../utils/htmlElement.utils';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from '../../../grid/genericGridSmartViews/utils';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';

export class MisconfigurationsAppView extends BaseWebComponentView {
  public riskType = RiskTypes.MISCONFIGURATIONS;
  public filterDependencyMapping = {
    companyId: ['affectedDevices', 'regions'],
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page__header .title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<Column, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$scanEmptyStateButton = this.shadow.querySelector('#scan-button-empty-state');
    this.dom.$scanEmptyStateTooltip = this.shadow.querySelector('#scan-button-empty-state-tooltip');
    this.dom.$overlay = this.shadow.querySelector('#extra-details-context-menu');
    this.dom.$disclaimer = {
      $box: this.shadow.querySelector('#disclaimer-info-box'),
      $text: this.shadow.querySelector('#disclaimer-info-box-text'),
    };
    this.dom.$deviceSelection = this.shadow.querySelector('#device-selection');

    this.setBulkActions();
    this.setTranslatedStrings();
    setComplianceDisclaimer(this.dom.$disclaimer);
  }

  setTranslatedStrings() {
    setTranslatedStrings.call(this);
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Findings');
  }

  async buildSuccessView(data: MisconfigurationsAppWebComponentSchema) {
    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
    });
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_misconfigurations: translateFn('All findings'),
        misconfigurations_high_severity: translateFn('High risk score'),
        misconfigurations_detected: translateFn('Detected'),
        misconfigurations_ignored: translateFn('Ignored'),
        misconfigurations_watchlist: this.dependencies.translateFn('Watchlist'),
        misconfigurations_kubernetes: translateFn('Kubernetes'),
      },
    };
  }

  private async buildSmartViews() {
    this.dependencies.componentName = 'misconfigurations-grid';
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      this.dependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      this.dependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    // Affected device type column should not be displayed
    view.displayedColumns = view.displayedColumns.filter((column: Column) => column !== Column.AFFECTED_DEVICES_TYPE);

    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;
    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);
    this.dom.$grid.dataProvider = new MisconfigurationsAppGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = this.getGridColumns();
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.MISCONFIGURATION_NAME]: misconfigurationNameRenderer.bind(this),
      [ColumnKeys.RISK_SCORE]: riskScoreRenderer.bind(this),
      [ColumnKeys.AFFECTED_DEVICES]: affectedDevicesRenderer.bind(this),
      [ColumnKeys.OS]: osRenderer.bind(this),
      [ColumnKeys.REGIONS]: regionsRenderer.bind(this),
      [ColumnKeys.COMPLIANCE]: complianceRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
    };
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.filterDependencyMapping = this.filterDependencyMapping;
    this.dom.$grid.propertiesSet = true;
    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
  }

  private getPredefinedFiltersKeys() {
    const keys: any = [];

    if (!Application.Main.company.isCustomer()) {
      keys.push(ColumnKeys.COMPANY);
    }

    return [
      ...keys,
      ColumnKeys.MISCONFIGURATION_NAME,
      ColumnKeys.RISK_SCORE,
      ColumnKeys.AFFECTED_DEVICES,
      ColumnKeys.MISCONFIGURATION_TYPE,
    ];
  }

  protected getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columns: any = [
      {
        column: Column.MISCONFIGURATION_NAME,
        key: ColumnKeys.MISCONFIGURATION_NAME,
        columnName: translateFn('Finding name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: '100px',
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.MisconfigurationsNames,
          ignoredFieldsForFilters: ['label', 'type'],
        },
      },
      {
        column: Column.RISK_SCORE,
        key: ColumnKeys.RISK_SCORE,
        columnName: translateFn('Risk score'),
        type: ColumnType.RANGE,
        width: '100px',
        removable: false,
        sortable: true,
      },
      {
        column: Column.AFFECTED_DEVICES,
        key: ColumnKeys.AFFECTED_DEVICES,
        columnName: translateFn('Affected resources'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.MisconfigurationsAffectedDevices,
        },
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: getStatusColumnValues(),
        removable: false,
        sortable: false,
      },
      {
        column: Column.STATE,
        key: ColumnKeys.STATE,
        columnName: translateFn('State'),
        type: ColumnType.ENUM,
        values: getStateColumnValues(),
        removable: false,
        sortable: false,
      },
      {
        column: Column.OS,
        key: ColumnKeys.OS,
        columnName: translateFn('Platform'),
        type: ColumnType.ENUM,
        values: getOSTypeColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: Column.REGIONS,
        key: ColumnKeys.REGIONS,
        columnName: translateFn('Regions'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.MisconfigurationsRegions,
        },
      },
      {
        column: Column.MISCONFIGURATION_TYPE,
        key: ColumnKeys.MISCONFIGURATION_TYPE,
        columnName: translateFn('Finding type'),
        type: ColumnType.ENUM,
        values: this.getMisconfigurationTypeColumnValues(),
        removable: true,
        sortable: false,
      },
      {
        column: Column.MITIGATION_TYPE,
        key: ColumnKeys.MITIGATION_TYPE,
        columnName: translateFn('Mitigation type'),
        type: ColumnType.ENUM,
        values: getMitigationTypeColumnValues(),
        removable: true,
        sortable: false,
      },
      {
        column: Column.COMPLIANCE,
        key: ColumnKeys.COMPLIANCE,
        columnName: translateFn('Compliance'),
        type: ColumnType.ENUM,
        values: getComplianceColumnValues(),
        removable: true,
        sortable: false,
      },
      {
        column: Column.AFFECTED_DEVICES_TYPE,
        key: ColumnKeys.AFFECTED_DEVICES_TYPE,
        columnName: this.dependencies.translateFn('Affected resource'),
        type: ColumnType.ENUM,
        values: getAffectedDeviceTypeColumnValues(),
        width: '100px',
        removable: false,
        sortable: false,
        isHiddenInGridSettings: true,
      },
      {
        column: Column.WATCHLIST,
        key: ColumnKeys.WATCHLIST,
        columnName: this.dependencies.translateFn('In watchlist'),
        type: ColumnType.ENUM,
        values: getWatchlistColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: Column.SELECT,
        key: ColumnKeys.SELECT,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
        width: '100px',
        filteringDisabled: true,
      },
    ];

    if (Application.Main.company.isPartner()) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: (Application.Main as any).user.company.id,
        },
      });
    }

    return columns;
  }

  private getMisconfigurationTypeColumnValues() {
    const values = [
      MisconfigurationType.BROWSER,
      MisconfigurationType.OS,
      MisconfigurationType.VULNERABILITY,
      MisconfigurationType.NETWORK,
      MisconfigurationType.CLOUD,
      MisconfigurationType.ACCESS_CONTROL,
      MisconfigurationType.CONTROL_PLANE,
      MisconfigurationType.SECRETS,
      MisconfigurationType.WORKLOAD,
    ];

    return values.map(value => {
      return {
        label: BaseWebComponentTranslator.getInstance().getMisconfigurationType(value),
        value,
      };
    });
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.AFFECTED_DEVICES,
        config: {
          filterLabel: this.dependencies.translateFn('Resource name'),
        },
      },
    ];
  }

  private setBulkActions() {
    this.dom.fixBulk = {
      $select: this.shadow.querySelector('#fix-bulk-select'),
      $tooltip: this.shadow.querySelector('#fix-bulk-tooltip'),
      $text: this.shadow.querySelector('#fix-bulk-button-text'),
    };
    this.dom.fixBulk.$text.innerText = translateFn('Fix risks');

    this.dom.fixBulk.$select.options = [
      {
        title: translateFn('Fix risks'),
        value: FixStateBulkValues.FIX_RISKS,
      },
      {
        title: translateFn('Roll back fixes'),
        value: FixStateBulkValues.ROLLBACK_FIXES,
      },
    ];

    setBulkActions.call(this, {
      ignore: translateFn('Ignore risks'),
      restore: translateFn('Restore ignored risks'),
    });
  }
}
